import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Always use same-origin to avoid cross-domain issues
// REACT_APP_BACKEND_URL can be stale in deployed environments
const API = window.location.origin;

// GLOBAL flags to prevent multiple instances from creating duplicate intervals
let _globalBannerIntervalCreated = false;
let _globalHeartbeatIntervalCreated = false;
let _globalBannerIntervalId = null;
let _globalHeartbeatIntervalId = null;

const AnnouncementBanner = ({ currentPage }) => {
  const [banner, setBanner] = useState(null);
  const [shouldShow, setShouldShow] = useState(false);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(null);
  
  const fetchApiKeyConfigured = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const res = await axios.get(`${API}/api/user/api-key-configured`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000,
      });
      if (res && typeof res.data?.api_key_configured === 'boolean') {
        setApiKeyConfigured(res.data.api_key_configured);
      }
    } catch (e) {
      // non-blocking
    }
  };

  const fetchBannerSettings = async () => {
    // Debounce: prevent fetching more than once per 30 seconds
    const now = Date.now();
    if (window._lastBannerFetch && (now - window._lastBannerFetch) < 30000) {
      return;
    }
    window._lastBannerFetch = now;
    
    try {
      const response = await axios.get(`${API}/api/system/settings/public`, { timeout: 5000 });
      if (response.data && response.data.status === 'success') {
        setBanner(response.data.settings.banner);
      }
    } catch (error) {
      // Non-blocking; avoid polluting console with transient network noise
      console.warn('Failed to fetch banner settings:', error?.message || error);
    }
  };

  useEffect(() => {
    // GLOBAL check - only one banner instance should create the interval
    if (_globalBannerIntervalCreated) return;
    _globalBannerIntervalCreated = true;
    
    fetchBannerSettings();
    fetchApiKeyConfigured();

    // Refresh banner settings every 5 MINUTES (was 60s)
    _globalBannerIntervalId = setInterval(fetchBannerSettings, 300000);
    return () => {
      // Don't clear on unmount - keep global interval running
    };
    // eslint-disable-next-line
  }, []);

  // Heartbeat keepalive: ping /api/ready every 2 MINUTES (was 60s)
  useEffect(() => {
    // GLOBAL check - only one banner instance should create the interval
    if (_globalHeartbeatIntervalCreated) return;
    _globalHeartbeatIntervalCreated = true;
    
    const ping = async () => {
      try {
        if (document.visibilityState !== 'visible') return;
        await axios.get(`${API}/api/ready`, { timeout: 4000 });
      } catch (_) {
        // silent
      }
    };
    // Initial ping and interval - 2 MINUTES instead of 60s
    ping();
    _globalHeartbeatIntervalId = setInterval(ping, 120000);

    const onVis = () => {
      if (document.visibilityState === 'visible') ping();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      // Don't clear on unmount - keep global interval running
    };
  }, []);

  useEffect(() => {
    if (!banner) {
      setShouldShow(false);
      return;
    }

    // Hide the specific API key prompt if user already configured a key
    if (apiKeyConfigured === true && banner?.text) {
      const lower = String(banner.text).toLowerCase();
      if (
        lower.includes('configure') &&
        lower.includes('trading212') &&
        lower.includes('api key')
      ) {
        setShouldShow(false);
        return;
      }
    }

    if (!banner.enabled) {
      setShouldShow(false);
      return;
    }

    // Determine if banner should show on current page
    const location = banner.location;
    if (location === 'off') {
      setShouldShow(false);
    } else if (location === 'everywhere') {
      setShouldShow(true);
    } else if (location === 'home' && currentPage === 'home') {
      setShouldShow(true);
    } else if (location === 'dashboard' && currentPage === 'dashboard') {
      setShouldShow(true);
    } else {
      setShouldShow(false);
    }
  }, [banner, currentPage, apiKeyConfigured]);

  if (!shouldShow || !banner || !banner.text) {
    return null;
  }

  const getBannerColor = () => {
    switch (banner.type) {
      case 'warning':
        return 'bg-yellow-600 text-gray-900';
      case 'success':
        return 'bg-green-600 text-white';
      case 'info':
      default:
        return 'bg-blue-600 text-white';
    }
  };

  return (
    <div className={`${getBannerColor()} py-2 overflow-hidden relative`}>
      <div className="marquee-container">
        <div className="marquee-content">
          <span className="font-semibold">{banner.text}</span>
        </div>
        <div className="marquee-content" aria-hidden="true">
          <span className="font-semibold">{banner.text}</span>
        </div>
      </div>
      <style jsx>{`
        .marquee-container {
          display: flex;
          white-space: nowrap;
        }
        .marquee-content {
          display: inline-block;
          padding-left: 100%;
          animation: marquee 20s linear infinite;
        }
        @keyframes marquee {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(-100%, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default AnnouncementBanner;
